import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { useI18n } from "../i18n";
import { logger } from "../lib/logger";

// Captures `beforeinstallprompt` so we can fire it from our own button.
// Renders nothing if the browser doesn't support PWA install (iOS Safari,
// older browsers) or the app is already installed.
export default function InstallPromptButton() {
    const { t } = useI18n();
    const [deferred, setDeferred] = useState(null);
    const [installed, setInstalled] = useState(false);

    useEffect(() => {
        // Already running standalone? Hide entirely.
        const standalone = window.matchMedia?.("(display-mode: standalone)").matches
            || window.navigator?.standalone === true;
        if (standalone) {
            setInstalled(true);
            return undefined;
        }

        const onBefore = (e) => {
            e.preventDefault();
            setDeferred(e);
        };
        const onInstalled = () => {
            setInstalled(true);
            setDeferred(null);
        };
        window.addEventListener("beforeinstallprompt", onBefore);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBefore);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    if (installed || !deferred) return null;

    const onClick = async () => {
        try {
            await deferred.prompt();
            const choice = await deferred.userChoice;
            logger.info("[pwa] install choice:", choice?.outcome);
        } catch (e) {
            logger.warn("[pwa] install prompt failed", e);
        } finally {
            setDeferred(null);
        }
    };

    return (
        <button
            onClick={onClick}
            data-testid="pwa-install-btn"
            title={t("pwa.install.title")}
            className="hidden sm:inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest text-[var(--accent-text)] bg-[var(--accent-hex)] hover:bg-[var(--accent-hex-hover)] px-2.5 py-1.5 rounded-md transition-colors"
        >
            <Download className="h-3 w-3" />
            {t("pwa.install.button")}
        </button>
    );
}
